/**
 * @Author: Florian Merel <florian>
 * @Date:   02-Nov-2020
 * @Email:  florian.merel@prokov.com
 * @Project: ComEditServer
 * @Filename: authentification.js
 * @Last modified by:   florian
 * @Last modified time: 25-Jan-2021
 */

import axios from "axios";
import server from "../server.js";
import servicesError from "../ServicesError.js";
import {
  persistPartnerFeatures,
  readPartnerFeatures,
  clearPartnerFeatures as clearStoredPartnerFeatures,
} from "../partnerFeatures.js";

/**
 * Écrit les flags partenaire dans le state ET dans le localStorage.
 * Le state porte la réactivité : le localStorage n'en a aucune, un computed qui le lit
 * serait mis en cache et ne verrait jamais le rafraîchissement du heartbeat. Le
 * localStorage assure la persistance au rechargement de page.
 */
function applyPartnerFeatures(context, features) {
  const value = features && typeof features === "object" ? features : {};
  persistPartnerFeatures(value);
  context.partnerFeatures = value;
}

const state = {
  loading: false,
  token: null,
  error: {},
  data: {},
  droits: null,
  // Initialisé depuis le localStorage pour survivre à un rechargement de page, puis
  // rafraîchi par le login et par le heartbeat (/tokenIsValid, toutes les 30 s).
  partnerFeatures: readPartnerFeatures(),
};

const getters = {
  getLogin(context) {
    return context.data;
  },

  getToken(context) {
    context.token = localStorage.getItem("token");
    return context.token;
  },

  getData(context) {
    return context.data;
  },

  getDroits(context) {
    return localStorage.getItem("droits");
  },

  getIsAssistance(context) {
    const id = localStorage.getItem("id");
    const ar_ids = [21, 15, 14, 5, 16];
    if (id) {
      return ar_ids.includes(parseInt(id));
    }
    return false;
  },

  getEmail(context) {
    return localStorage.getItem("email");
  },

  getId(context) {
    return localStorage.getItem("id");
  },

  getPartnerCode(context) {
    return localStorage.getItem("partnerCode") || null;
  },

  // Flags métier du partenaire. Lus depuis le state et non depuis le localStorage :
  // c'est ce qui rend les composants réactifs au rafraîchissement du heartbeat.
  getPartnerFeatures(context) {
    return context.partnerFeatures || {};
  },
};

const mutations = {
  setResponse(context, data) {
    context.error = null;
    context.data = data;
    if (data && data.data) {
      if (data.data.id) {
        localStorage.setItem("id", data.data.id);
      }

      if (data.data.droits) {
        localStorage.setItem("droits", data.data.droits);
        context.droits = data.data.droits;
      }

      if (data.data.email) {
        localStorage.setItem("email", data.data.email);
      }

      if (data.data.partnerCode) {
        localStorage.setItem("partnerCode", data.data.partnerCode);
      }

      if (data.data.partnerApps) {
        localStorage.setItem("partnerApps", data.data.partnerApps);
      }

      if (data.data.partnerLicenceLabel) {
        localStorage.setItem("partnerLicenceLabel", data.data.partnerLicenceLabel);
      }

      // Toujours réécrit, même absent de la réponse : sinon les flags d'un partenaire
      // précédent survivraient à une reconnexion.
      applyPartnerFeatures(context, data.data.partnerFeatures);

      if (data.data.version) {
        var v = localStorage.getItem("version");
        if (v != data.data.version) {
          localStorage.setItem("version", data.data.version);
          window.location.reload(true);
        }
      }
    }
  },

  setError(context, error) {
    context.data = null;
    context.error = error;
  },

  setLoading(context, loading) {
    context.loading = loading;
  },

  setToken(context, token) {
    context.token = token;
    localStorage.setItem("token", context.token);
  },

  // Appelée par le heartbeat à chaque /tokenIsValid : un changement de config partenaire
  // se propage aux écrans déjà ouverts sans rechargement.
  setPartnerFeatures(context, features) {
    applyPartnerFeatures(context, features);
  },

  clearPartnerFeatures(context) {
    clearStoredPartnerFeatures();
    context.partnerFeatures = {};
  },
};

const actions = {
  login(context, params) {
    return new Promise((resolve, reject) => {
      context.commit("setLoading", true);
      axios({
        method: "POST",
        url: server.url + server.urls.login,
        data: params,
      })
        .then((response) => {
          context.commit("setLoading", false);
          if (response.data) {
            context.commit("setResponse", response.data);
          }
          resolve(response);
        })
        .catch((error) => {
          context.commit("setLoading", false);
          if (error.response) {
            context.commit("setError", error.response.data);
          }
          // Rejette l'erreur axios complète : error.response vaut undefined quand la
          // requête n'a atteint aucun serveur, et l'appelant doit pouvoir le distinguer.
          reject(error);
        });
    });
  },

  async getTokenIsValid(context) {
    context.commit("setLoading", true);
    try {
      let res = await axios({
        method: "GET",
        url: server.url + server.urls.tokenIsValid,
        headers: server.getHeader(),
      });
      context.commit("setLoading", false);
      context.commit("setResponse", res.data);
      return res.data;
    } catch (error) {
      context.commit("setLoading", false);

      // Sans réponse (serveur injoignable, requête bloquée), il n'y a rien à analyser :
      // lire error.response.data lèverait une TypeError dans le catch.
      if (!error.response) {
        return null;
      }

      context.commit("setError", error.response.data);
      servicesError.analyseErrorService(error.response.data);
      return error.response.data;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
